import { useEffect, useState } from 'react';
import { Link, useBlocker, useParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import {
  getEmailTemplate, listEmailTemplates, previewEmailTemplate, resetEmailTemplate, saveEmailTemplate, sendTestEmailTemplate,
} from '@/lib/api/admin';
import { ApiError, describeError } from '@/lib/api/client';
import type { EmailLocale, EmailTemplateContent, EmailTemplateDetail, EmailTemplateKind } from '@/lib/api/types';
import { cn } from '@/lib/cn';
import { formatDateTime } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Notice, QueryError, SkeletonRows } from '@/components/ui/feedback';
import { Field, inputClass } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page';
import { Tabs } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { EMAIL_LOCALES, FIELD_LABEL, LOCALE_LABEL, insertAt, sameContent, templateError } from './editor';

type FieldKey = keyof EmailTemplateContent;
type FieldElement = HTMLInputElement | HTMLTextAreaElement;

const LEAVE_PROMPT = 'Есть несохранённые правки. Перейти без сохранения?';

/** One email kind: its wording per language, a live preview, a test send to yourself. */
export function EmailTemplatePage() {
  const { kind = '' } = useParams();
  const [locale, setLocale] = useState<EmailLocale>('ru');
  const [dirty, setDirty] = useState(false);
  const kinds = useQuery({ queryKey: ['admin', 'email-templates'], queryFn: listEmailTemplates });
  const spec = kinds.data?.find((k) => k.kind === kind);

  const pickLocale = (next: EmailLocale) => {
    if (next === locale) return;
    if (dirty && !window.confirm(LEAVE_PROMPT)) return;
    setDirty(false);
    setLocale(next);
  };

  return (
    <>
      <Link to="/email-templates" className="mb-3 inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink">
        <ArrowLeft size={14} /> Все письма
      </Link>
      <PageHeader title={spec?.title ?? 'Письмо'} subtitle={kind} />
      {kinds.isPending ? <SkeletonRows rows={4} cols={2} />
        : kinds.error ? <QueryError error={kinds.error} onRetry={() => kinds.refetch()} />
        : !spec ? <Notice tone="warn" title="Такого письма нет">Вернитесь к списку писем.</Notice>
        : (
          <>
            <Tabs
              className="mb-4"
              value={locale}
              onChange={pickLocale}
              tabs={EMAIL_LOCALES.map((l) => ({ value: l, label: spec.customized[l] ? `${LOCALE_LABEL[l]} · изменён` : LOCALE_LABEL[l] }))}
            />
            <LocaleEditor key={`${kind}/${locale}`} spec={spec} locale={locale} onDirtyChange={setDirty} />
          </>
        )}
    </>
  );
}

function LocaleEditor({ spec, locale, onDirtyChange }: {
  spec: EmailTemplateKind;
  locale: EmailLocale;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const detail = useQuery({
    queryKey: ['admin', 'email-template', spec.kind, locale],
    queryFn: () => getEmailTemplate(spec.kind, locale),
  });
  if (detail.isPending) return <SkeletonRows rows={6} cols={2} />;
  if (detail.error) return <QueryError error={detail.error} onRetry={() => detail.refetch()} />;
  return <EditorForm spec={spec} locale={locale} detail={detail.data} onDirtyChange={onDirtyChange} />;
}

/** Follows `value`, `ms` after it stops changing. */
function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}

function EditorForm({ spec, locale, detail, onDirtyChange }: {
  spec: EmailTemplateKind;
  locale: EmailLocale;
  detail: EmailTemplateDetail;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<EmailTemplateContent>(detail.content);
  const [fieldErr, setFieldErr] = useState<{ field: FieldKey; message: string } | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  // Where a clicked variable goes: the field the person was last in.
  const [lastFocused, setLastFocused] = useState<FieldKey>('bodyMd');
  const dirty = !sameContent(draft, detail.content);

  useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname);

  const debounced = useDebounced(draft, 400);
  const preview = useQuery({
    queryKey: ['admin', 'email-preview', spec.kind, locale, debounced],
    queryFn: () => previewEmailTemplate(spec.kind, locale, debounced),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const previewErr = preview.error ? templateError(preview.error) : null;

  const setField = (key: FieldKey, value: string) => {
    setDraft((d) => ({ ...d, [key]: value }));
    if (fieldErr?.field === key) setFieldErr(null);
  };

  const insertVariable = (name: string) => {
    const key = lastFocused;
    const el = document.getElementById(`tpl-${key}`) as FieldElement | null;
    const value = draft[key];
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = insertAt(value, start, end, `{{${name}}}`);
    setField(key, next.value);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(next.caret, next.caret);
    });
  };

  const refreshList = () => void qc.invalidateQueries({ queryKey: ['admin', 'email-templates'] });

  const save = useMutation({
    mutationFn: () => saveEmailTemplate(spec.kind, locale, draft),
    onSuccess: (saved) => {
      toast('success', 'Сохранено', 'Следующие письма уйдут с этим текстом (в течение 30 секунд).');
      qc.setQueryData(['admin', 'email-template', spec.kind, locale], saved);
      setFieldErr(null);
      refreshList();
    },
    onError: (err) => {
      const mapped = templateError(err);
      if (mapped) setFieldErr(mapped);
      else toast('error', 'Не удалось сохранить', describeError(err));
    },
  });

  const test = useMutation({
    mutationFn: () => sendTestEmailTemplate(spec.kind, locale, draft),
    onSuccess: ({ to }) => toast('success', 'Тестовое письмо отправлено', to),
    onError: (err) => {
      const mapped = templateError(err);
      if (mapped) setFieldErr(mapped);
      else if (ApiError.is(err) && err.statusCode === 429) toast('error', 'Не чаще 10 писем в минуту');
      else toast('error', 'Не удалось отправить', describeError(err));
    },
  });

  const reset = useMutation({
    mutationFn: () => resetEmailTemplate(spec.kind, locale),
    onSuccess: () => {
      toast('success', 'Возвращён исходный текст');
      qc.setQueryData<EmailTemplateDetail>(['admin', 'email-template', spec.kind, locale], {
        ...detail, content: detail.defaults, customized: false, updatedAt: null, updatedByEmail: null,
      });
      setDraft(detail.defaults);
      setConfirmReset(false);
      setFieldErr(null);
      refreshList();
    },
    onError: (err) => toast('error', 'Не удалось сбросить', describeError(err)),
  });

  const fieldProps = (key: FieldKey) => ({
    id: `tpl-${key}`,
    value: draft[key],
    onFocus: () => setLastFocused(key),
    onChange: (e: React.ChangeEvent<FieldElement>) => setField(key, e.target.value),
    'aria-invalid': fieldErr?.field === key || undefined,
  });
  const errorOf = (key: FieldKey) => (fieldErr?.field === key ? fieldErr.message : undefined);
  const area = cn(inputClass, 'h-auto py-2 font-mono text-[13px] leading-5');

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <Card className="space-y-4">
        <Field label={FIELD_LABEL.subject} htmlFor="tpl-subject" error={errorOf('subject')}>
          <input className={inputClass} {...fieldProps('subject')} />
        </Field>
        <Field label={FIELD_LABEL.heading} htmlFor="tpl-heading" error={errorOf('heading')}>
          <input className={inputClass} {...fieldProps('heading')} />
        </Field>
        <Field
          label={FIELD_LABEL.bodyMd}
          htmlFor="tpl-bodyMd"
          error={errorOf('bodyMd')}
          hint="Markdown: **жирный**, *курсив*, [ссылка](https://…), «- » для списка, пустая строка — новый абзац."
        >
          <textarea rows={10} className={area} {...fieldProps('bodyMd')} />
        </Field>
        {spec.hasAction && (
          <Field label={FIELD_LABEL.actionLabel} htmlFor="tpl-actionLabel" error={errorOf('actionLabel')} hint="Адрес кнопки задаёт система.">
            <input className={inputClass} {...fieldProps('actionLabel')} />
          </Field>
        )}
        <Field label={FIELD_LABEL.notesMd} htmlFor="tpl-notesMd" error={errorOf('notesMd')}>
          <textarea rows={3} className={area} {...fieldProps('notesMd')} />
        </Field>

        <div className="flex flex-wrap gap-2 border-t border-line pt-4">
          <Button disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>Сохранить</Button>
          <Button variant="outline" disabled={!dirty} onClick={() => { setDraft(detail.content); setFieldErr(null); }}>Отменить правки</Button>
          <Button variant="soft" loading={test.isPending} onClick={() => test.mutate()}>Отправить тест себе</Button>
          {detail.customized && (
            <Button variant="ghost" className="ml-auto" onClick={() => setConfirmReset(true)}>Сбросить к исходному</Button>
          )}
        </div>
        <p className="text-xs text-faint">
          {detail.customized && detail.updatedAt
            ? `Изменено ${formatDateTime(detail.updatedAt)} · ${detail.updatedByEmail ?? '—'}`
            : 'Сейчас действует исходный текст из кода.'}
        </p>
      </Card>

      <div className="space-y-4">
        {spec.variables.length > 0 && (
          <Card>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted">Переменные — клик вставляет в поле</div>
            <ul className="space-y-1">
              {spec.variables.map((v) => (
                <li key={v.name}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insertVariable(v.name)}
                    className="w-full rounded-lg px-2 py-1.5 text-left hover:bg-surface-2"
                  >
                    <code className="text-sm text-accent-ink">{`{{${v.name}}}`}</code>
                    <span className="ml-2 text-sm text-ink">{v.description}</span>
                    <span className="block text-xs text-faint">
                      напр. {v.example}{v.optional ? ' · может отсутствовать — тогда строка с ней не выводится' : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        )}

        <Card>
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted">Предпросмотр</div>
            {preview.isFetching && <span className="text-xs text-faint">обновляю…</span>}
          </div>
          {previewErr && <Notice tone="danger" className="mb-3" title={`${FIELD_LABEL[previewErr.field]}: ${previewErr.message}`} />}
          {preview.error && !previewErr && <Notice tone="danger" className="mb-3" title="Предпросмотр недоступен">{describeError(preview.error)}</Notice>}
          {preview.data && (
            <>
              <p className="mb-2 text-sm text-ink"><span className="text-muted">Тема:</span> {preview.data.subject}</p>
              <iframe title="Предпросмотр письма" sandbox="" srcDoc={preview.data.html} className="h-[640px] w-full rounded-lg border border-line bg-white" />
            </>
          )}
        </Card>
      </div>

      {confirmReset && (
        <Dialog
          open
          onClose={() => setConfirmReset(false)}
          title="Сбросить к исходному?"
          description={`Вернётся текст из кода для языка «${LOCALE_LABEL[locale]}». Ваши правки этого языка удалятся.`}
          footer={(
            <>
              <Button variant="ghost" onClick={() => setConfirmReset(false)}>Отмена</Button>
              <Button variant="danger" loading={reset.isPending} onClick={() => reset.mutate()}>Сбросить</Button>
            </>
          )}
        >
          <div />
        </Dialog>
      )}

      {blocker.state === 'blocked' && (
        <Dialog
          open
          onClose={() => blocker.reset()}
          title="Есть несохранённые правки"
          description="Если уйти сейчас, правки этого языка пропадут."
          footer={(
            <>
              <Button variant="ghost" onClick={() => blocker.reset()}>Остаться</Button>
              <Button variant="danger" onClick={() => blocker.proceed()}>Уйти без сохранения</Button>
            </>
          )}
        >
          <div />
        </Dialog>
      )}
    </div>
  );
}
